import React, { ChangeEvent, FC, useId } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ListSortOption } from '../../../common/model/ui/list-sort-option';
import { OrderBy } from '../../../common/types';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvListSortSelectProps {
  options: ListSortOption[];
  orderBy: string;
  orderDir: string;
  onChange: (order: OrderBy) => void;
  disabledReason?: string;
}

const CHEVRON_ICON = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${Colours.OV_BASE}"><path d="m7 10 5 5 5-5z"/></svg>`
)}`;

const getOptionValue = (orderBy: string, orderDir: string) =>
  `${orderBy}:${orderDir}`;

const OvListSortSelect: FC<OvListSortSelectProps> = ({
  options,
  orderBy,
  orderDir,
  onChange,
  disabledReason = '',
}) => {
  const { t } = useTranslation();
  const selectId = useId();
  const reasonId = useId();
  const value = getOptionValue(orderBy, orderDir);
  const hasSelectedOption = options.some(
    (option) => getOptionValue(option.order_by, option.order_dir) === value
  );

  const onSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = options.find(
      (option) =>
        getOptionValue(option.order_by, option.order_dir) === event.target.value
    );

    if (selectedOption) {
      onChange({
        order_by: selectedOption.order_by,
        order_dir: selectedOption.order_dir,
      });
    }
  };

  return (
    <Wrapper $hasReason={!!disabledReason}>
      <label htmlFor={selectId}>{t('common.listSort.label')}</label>
      <select
        id={selectId}
        value={value}
        disabled={!!disabledReason}
        aria-describedby={disabledReason ? reasonId : undefined}
        onChange={onSelect}
      >
        {!hasSelectedOption && (
          <option value={value}>
            {orderBy || t('common.listSort.default')}
          </option>
        )}
        {options.map((option) => (
          <option
            key={getOptionValue(option.order_by, option.order_dir)}
            value={getOptionValue(option.order_by, option.order_dir)}
          >
            {option.label}
          </option>
        ))}
      </select>
      {disabledReason && <Reason id={reasonId}>{disabledReason}</Reason>}
    </Wrapper>
  );
};

export default OvListSortSelect;

const Wrapper = styled.div<{ $hasReason: boolean }>`
  display: flex;
  flex-wrap: ${({ $hasReason }) => ($hasReason ? 'wrap' : 'nowrap')};
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_BASE};

  select {
    min-height: 2.125rem;
    max-width: 13rem;
    padding: 0.4375rem 2rem 0.4375rem 0.625rem;
    border: 1px solid ${Colours.OV_CHARCOAL};
    border-radius: ${Variables.borderRadius.SMALL};
    appearance: none;
    background: ${Colours.WHITE} url('${CHEVRON_ICON}') no-repeat right 0.5rem
      center / 1.25rem;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  select:disabled {
    border-color: ${Colours.OV_BORDER_COLOR_V3};
    color: ${Colours.OV_CHARCOAL};
    cursor: not-allowed;
  }

  select:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;

const Reason = styled.span`
  flex-basis: 100%;
  text-align: right;
  color: ${Colours.OV_CHARCOAL};
`;
